import React, { useState, useEffect, useRef } from "react";
import { GripVertical } from "lucide-react";
import { BucketModel } from "@/database";
import "./BucketOrderManager.scss";

interface BucketOrderManagerProps {
  buckets: BucketModel[];
  initialOrder?: string[];
  onChange: (orderedIds: string[]) => void;
  label?: string;
  showRequired?: boolean;
  currentProduct?: { id: string };
}

interface OrderedBucket {
  id: string;
  name: string;
  order: number;
}

export const BucketOrderManager: React.FC<BucketOrderManagerProps> = ({
  buckets,
  initialOrder = [],
  onChange,
  label = "Bucket Order",
  showRequired = false,
  currentProduct,
}) => {
  const [orderedBuckets, setOrderedBuckets] = useState<OrderedBucket[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const previousOrderRef = useRef<string>("");

  // Initialize ordered buckets from initialOrder or available buckets
  useEffect(() => {
    if (!buckets || buckets.length === 0) {
      setOrderedBuckets([]);
      return;
    }

    // Filter buckets that match the currentProduct.id
    const filteredBuckets = currentProduct?.id
      ? buckets.filter(
          (bucket) => bucket.team && bucket.team.includes(currentProduct.id)
        )
      : buckets;

    // Create a map of bucket ID to bucket for quick lookup
    const bucketMap = new Map(
      filteredBuckets.map((bucket) => [bucket.id || "", bucket])
    );

    let ordered: OrderedBucket[] = [];

    if (initialOrder && initialOrder.length > 0) {
      // Use initialOrder to set the order
      ordered = initialOrder
        .map((id, index) => {
          const bucket = bucketMap.get(id);
          if (bucket && bucket.id) {
            return {
              id: bucket.id,
              name: bucket.name || "Unnamed Bucket",
              order: index,
            };
          }
          return null;
        })
        .filter((item): item is OrderedBucket => item !== null);

      // Add any buckets that are in the filteredBuckets list but not in initialOrder
      filteredBuckets.forEach((bucket) => {
        if (
          bucket.id &&
          !initialOrder.includes(bucket.id) &&
          !ordered.find((ob) => ob.id === bucket.id)
        ) {
          ordered.push({
            id: bucket.id,
            name: bucket.name || "Unnamed Bucket",
            order: ordered.length,
          });
        }
      });
    } else {
      // No initial order, use filteredBuckets as they are
      ordered = filteredBuckets
        .filter((bucket) => bucket.id)
        .map((bucket, index) => ({
          id: bucket.id!,
          name: bucket.name || "Unnamed Bucket",
          order: index,
        }));
    }

    setOrderedBuckets(ordered);
  }, [buckets, initialOrder, currentProduct]);

  // Notify parent of order changes only when the order actually changes
  useEffect(() => {
    const orderedIds = orderedBuckets.map((bucket) => bucket.id);
    const currentOrderString = orderedIds.join(",");

    // Only call onChange if the order has actually changed
    if (previousOrderRef.current !== currentOrderString) {
      previousOrderRef.current = currentOrderString;
      onChange(orderedIds);
    }
  }, [orderedBuckets]);

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number
  ) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.currentTarget.innerHTML);

    // Add a visual cue
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.opacity = "1";
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (
    e: React.DragEvent<HTMLDivElement>,
    index: number
  ) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // Only clear if we're leaving the container
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    dropIndex: number
  ) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const newOrderedBuckets = [...orderedBuckets];
    const [draggedItem] = newOrderedBuckets.splice(draggedIndex, 1);
    newOrderedBuckets.splice(dropIndex, 0, draggedItem);

    // Update order numbers
    const updatedBuckets = newOrderedBuckets.map((bucket, index) => ({
      ...bucket,
      order: index,
    }));

    setOrderedBuckets(updatedBuckets);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Commented out as it's not currently used
  // const handleRemove = (id: string) => {
  //   const filtered = orderedBuckets
  //     .filter((bucket) => bucket.id !== id)
  //     .map((bucket, index) => ({
  //       ...bucket,
  //       order: index,
  //     }));
  //   setOrderedBuckets(filtered);
  // };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newOrderedBuckets = [...orderedBuckets];
    [newOrderedBuckets[index - 1], newOrderedBuckets[index]] = [
      newOrderedBuckets[index],
      newOrderedBuckets[index - 1],
    ];
    const updatedBuckets = newOrderedBuckets.map((bucket, idx) => ({
      ...bucket,
      order: idx,
    }));
    setOrderedBuckets(updatedBuckets);
  };

  const moveDown = (index: number) => {
    if (index === orderedBuckets.length - 1) return;
    const newOrderedBuckets = [...orderedBuckets];
    [newOrderedBuckets[index], newOrderedBuckets[index + 1]] = [
      newOrderedBuckets[index + 1],
      newOrderedBuckets[index],
    ];
    const updatedBuckets = newOrderedBuckets.map((bucket, idx) => ({
      ...bucket,
      order: idx,
    }));
    setOrderedBuckets(updatedBuckets);
  };

  if (!buckets || buckets.length === 0) {
    return (
      <div className="bucket-order-manager">
        <label className="form-label">
          {label}
          {showRequired && <span className="text-danger ms-1">*</span>}
        </label>
        <div className="text-muted text-center py-4">
          No buckets available to order
        </div>
      </div>
    );
  }

  return (
    <div className="bucket-order-manager">
      <label className="form-label">
        {/* {label} */}
        {showRequired && <span className="text-danger ms-1">*</span>}
      </label>
      {/* <div className="bucket-order-info mb-2">
        <small className="text-muted">
          Drag and drop to reorder buckets. The order will determine how they appear in the UI.
        </small>
      </div> */}

      {orderedBuckets.length === 0 ? (
        <div className="text-muted text-center py-4">
          All buckets have been removed. Add some buckets to get started.
        </div>
      ) : (
        <div className="bucket-order-list">
          {orderedBuckets.map((bucket, index) => (
            <div
              key={bucket.id}
              className={`bucket-order-item ${
                draggedIndex === index ? "dragging" : ""
              } ${dragOverIndex === index ? "drag-over" : ""}`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
            >
              <div className="bucket-order-grip">
                <GripVertical size={20} className="text-muted" />
              </div>

              <div className="bucket-order-number">{index + 1}</div>

              <div className="bucket-order-name flex-grow-1">{bucket.name}</div>

              <div className="bucket-order-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => moveDown(index)}
                  disabled={index === orderedBuckets.length - 1}
                  title="Move down"
                >
                  ↓
                </button>
                {/* <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleRemove(bucket.id)}
                  title="Remove from list"
                >
                  <Trash2 size={14} />
                </button> */}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BucketOrderManager;
